import axios from "axios";
import Config from "../../Config";

const getHeaders = () => ({
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
  },
});

const buildErrorMessage = (error, fallback) =>
  error?.response?.data?.data?.reason ||
  error?.response?.data?.reason ||
  error?.response?.data?.message ||
  error?.message ||
  fallback;

const reviewsPayload = (envelope) => {
  if (Array.isArray(envelope?.reviews)) return envelope;
  if (Array.isArray(envelope?.data?.reviews)) return envelope.data;
  if (Array.isArray(envelope?.data)) return { reviews: envelope.data, pagination: envelope.pagination || envelope.meta || null };
  if (Array.isArray(envelope)) return { reviews: envelope, pagination: null };
  return envelope && typeof envelope === "object" ? envelope : {};
};

const withPageBounds = (pagination, reviewCount) => {
  if (!pagination) return null;
  const current = Number(pagination.current_page) || 1;
  const perPage = Number(pagination.per_page) || reviewCount || 15;
  const total = Number(pagination.total);
  const safeTotal = Number.isFinite(total) ? total : reviewCount;
  const from = pagination.from ?? (safeTotal === 0 ? 0 : (current - 1) * perPage + 1);
  const to = pagination.to ?? Math.min(current * perPage, safeTotal);
  return {
    ...pagination,
    current_page: current,
    last_page: Number(pagination.last_page) || 1,
    per_page: perPage,
    total: safeTotal,
    from,
    to,
  };
};

export class ReviewsService {
  static async getReviews({ status, dealSlug, page = 1, perPage = 15 } = {}) {
    try {
      const response = await axios.get(`${Config.baseUrl}/reviews`, {
        ...getHeaders(),
        params: {
          page,
          per_page: perPage,
          ...(status ? { status } : {}),
          ...(dealSlug ? { deal_slug: dealSlug } : {}),
        },
      });

      const envelope = response.data?.data;
      if (envelope?.status_code && envelope.status_code !== "000") {
        return {
          success: false,
          message: envelope.reason || "Failed to fetch reviews",
          reviews: [],
          pagination: null,
        };
      }

      const payload = reviewsPayload(envelope);
      const reviews = Array.isArray(payload.reviews) ? payload.reviews : [];
      return {
        success: true,
        reviews,
        pagination: withPageBounds(payload.pagination || payload.meta || null, reviews.length),
      };
    } catch (error) {
      return {
        success: false,
        message: buildErrorMessage(error, "Failed to fetch reviews"),
        reviews: [],
        pagination: null,
      };
    }
  }

  static async approveReview(id) {
    try {
      const response = await axios.post(`${Config.baseUrl}/reviews/${id}/approve`, undefined, {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
        },
      });
      const envelope = response.data?.data;
      if (envelope?.status_code && envelope.status_code !== "000") {
        return { success: false, message: envelope.reason || "Could not approve this review" };
      }
      return { success: true, data: envelope?.data ?? envelope };
    } catch (error) {
      return { success: false, message: buildErrorMessage(error, "Could not approve this review") };
    }
  }

  static async rejectReview(id, rejectionReason) {
    try {
      const response = await axios.post(
        `${Config.baseUrl}/reviews/${id}/reject`,
        { rejection_reason: rejectionReason },
        getHeaders()
      );
      const envelope = response.data?.data;
      if (envelope?.status_code && envelope.status_code !== "000") {
        return { success: false, message: envelope.reason || "Could not reject this review" };
      }
      return { success: true, data: envelope?.data ?? envelope };
    } catch (error) {
      return { success: false, message: buildErrorMessage(error, "Could not reject this review") };
    }
  }
}

export default ReviewsService;
